import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Wordmark from '../components/Wordmark';
import { CELL_STYLES_PREVIEW } from '../styles/cellStyles';
import { sty } from './LandingPage.styles';

// ── Novels actually in the database (from migrations/seed.js) ────────────────
const SAMPLE_NOVELS = [
  { title: 'A Wrinkle in Time',   author: "Madeleine L'Engle", grade: 'Grades 5–6', words: '48 vocabulary words' },
  { title: 'Esperanza Rising',    author: 'Pam Muñoz Ryan',    grade: 'Grades 5–7', words: '40 vocabulary words' },
  { title: 'Frindle',             author: 'Andrew Clements',   grade: 'Grades 4–6', words: '36 vocabulary words' },
  { title: 'Holes',               author: 'Louis Sachar',      grade: 'Grades 5–7', words: '42 vocabulary words' },
  { title: 'Johnny Tremain',      author: 'Esther Forbes',     grade: 'Grades 6–8', words: '50 vocabulary words' },
  { title: 'The Lightning Thief', author: 'Rick Riordan',      grade: 'Grades 5–7', words: '44 vocabulary words' },
];

const FEATURES = [
  {
    icon: '🃏',
    iconBg: '#d0f0ed',
    heading: 'Every board is unique',
    body: 'Each student gets a randomized board. No copying, no free-riding. Everyone has to pay attention to win.',
  },
  {
    icon: '📚',
    iconBg: '#daeef4',
    heading: 'Vocab from your novels, ready to go',
    body: 'Pre-loaded word lists for popular K–12 novels. Upload your own custom list in seconds for anything else.',
  },
  {
    icon: '⚡',
    iconBg: '#ffe5e5',
    heading: 'Live games in under two minutes',
    body: 'Share a class code from any device. Students join instantly in any browser. No downloads, no accounts required.',
  },
];

const STEPS = [
  {
    n: '1', label: 'Pick a novel',
    desc: 'Choose from the library or upload your own vocabulary list. Pre-loaded lists include words and definitions — zero prep needed.',
  },
  {
    n: '2', label: 'Share the code',
    desc: 'One class code, written on the board or projected. Students join from phones, Chromebooks, or tablets in seconds.',
  },
  {
    n: '3', label: 'Call and play',
    desc: 'You call words or definitions, students mark their boards. Watch the live scoreboard as the game unfolds. First BINGO wins.',
  },
];

// ── Nav ───────────────────────────────────────────────────────────────────────
function Nav() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <nav style={sty.nav}>
        <style>{`
          @media (min-width: 641px) {
            .nav-desktop { display: flex !important; }
            .nav-burger  { display: none  !important; }
          }
          @media (max-width: 640px) {
            .nav-desktop { display: none  !important; }
            .nav-burger  { display: flex  !important; }
          }
          .ham-bar {
            display: block; width: 22px; height: 2px;
            background: #1B1B2F; border-radius: 2px;
            transition: transform 0.25s ease, opacity 0.2s ease;
          }
          .ham-top.open { transform: translateY(7px) rotate(45deg); }
          .ham-mid.open { opacity: 0; transform: scaleX(0); }
          .ham-bot.open { transform: translateY(-7px) rotate(-45deg); }
          .nav-overlay {
            position: fixed; top: 60px; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.4); z-index: 200;
            opacity: 0; pointer-events: none; transition: opacity 0.25s;
          }
          .nav-overlay.open { opacity: 1; pointer-events: auto; }
          .nav-drawer {
            position: fixed; top: 60px; right: 0;
            height: calc(100vh - 60px); width: 280px; max-width: 85vw;
            background: #fff; z-index: 201;
            box-shadow: -4px 0 24px rgba(0,0,0,0.1);
            transform: translateX(100%);
            transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
          }
          .nav-drawer.open { transform: translateX(0); }
          .drawer-link:hover { color: #006D77 !important; }
        `}</style>
        <div style={sty.navInner}>
          <Wordmark style={{ fontSize: '20px' }} />
          <div className="nav-desktop" style={sty.navLinks}>
            <Link to="/demo"   style={sty.navLink}>Try Demo</Link>
            <Link to="/login"  style={sty.navLink}>Sign In</Link>
          </div>
          <button
            className="nav-burger"
            style={sty.hamburgerBtn}
            onClick={() => setOpen(o => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            <span className={`ham-bar ham-top${open ? ' open' : ''}`} />
            <span className={`ham-bar ham-mid${open ? ' open' : ''}`} />
            <span className={`ham-bar ham-bot${open ? ' open' : ''}`} />
          </button>
        </div>
      </nav>

      <div className={`nav-overlay${open ? ' open' : ''}`} onClick={close} />
      <div className={`nav-drawer${open ? ' open' : ''}`}>
        <div style={sty.drawerContent}>
          <Link to="/demo"   className="drawer-link" style={sty.drawerLink} onClick={close}>Try Demo</Link>
          <Link to="/login"  className="drawer-link" style={sty.drawerLink} onClick={close}>Sign In</Link>
        </div>
      </div>
    </>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
const BINGO_CELLS = [
  { word: 'Olympus',   state: 'marked'  },
  { word: 'Medusa',    state: 'default' },
  { word: 'satyr',     state: 'marked'  },
  { word: 'oracle',    state: 'default' },
  { word: 'Chiron',    state: 'default' },
  { word: 'Minotaur',  state: 'default' },
  { word: 'cyclops',   state: 'marked'  },
  { word: 'Poseidon',  state: 'default' },
  { word: 'Athena',    state: 'marked'  },
  { word: 'demigod',   state: 'default' },
  { word: 'labyrinth', state: 'default' },
  { word: 'ambrosia',  state: 'marked'  },
  { word: 'FREE',      state: 'free'    },
  { word: 'Olympian',  state: 'marked'  },
  { word: 'Annabeth',  state: 'default' },
  { word: 'Grover',    state: 'default' },
  { word: 'Thalia',    state: 'marked'  },
  { word: 'ephemeral', state: 'marked'  },
  { word: 'prophecy',  state: 'default' },
  { word: 'Kronos',    state: 'default' },
  { word: 'Tartarus',  state: 'default' },
  { word: 'Elysium',   state: 'default' },
  { word: 'Hermes',    state: 'marked'  },
  { word: 'Percy',     state: 'default' },
  { word: 'Riptide',   state: 'marked'  },
];

const CELL_STYLES = CELL_STYLES_PREVIEW;

function Hero() {
  return (
    <section style={sty.hero}>
      <style>{`
        @media (max-width: 768px) {
          .hero-two-col { grid-template-columns: 1fr !important; }
          .hero-h1 { font-size: 38px !important; }
        }
      `}</style>
      <div className="hero-two-col" style={sty.heroGrid}>

        {/* Left — text */}
        <div>
          <div style={sty.heroBadge}>⚡ Live vocabulary games for the classroom</div>
          <h1 className="hero-h1" style={sty.heroH1}>
            Turn vocab review into a{' '}
            <span style={{ color: '#83C5BE' }}>game</span>{' '}
            they actually want to play
          </h1>
          <p style={sty.heroSub}>
            Real-time bingo built around your novel's vocabulary. Every student gets a unique board. Launch in under two minutes with no app and no student accounts required.
          </p>
          <div style={sty.heroCtaRow}>
            <Link to="/signup" style={sty.heroPrimary}>Subscribe for Full Access →</Link>
            <Link to="/demo" style={sty.heroSecondary}>Try Demo</Link>
            <Link to="/play?utm_source=tpt" style={sty.heroSecondary}>Have a TpT printable? Start here — it's free →</Link>
          </div>
        </div>

        {/* Right — bingo board snapshot */}
        <div style={sty.heroRight}>
          <div style={sty.boardFrame}>
            <div style={sty.boardHeader}>
              <span style={sty.boardClass}>Mrs. Garcia's Class</span>
              <span style={sty.boardClass}>Room: B7F3</span>
              <span style={sty.boardNovel}>The Lightning Thief</span>
            </div>
            <div style={sty.clueBox}>
              <div>
                <div style={sty.clueLabel}>CURRENT CLUE</div>
                <div style={sty.clueWord}>ephemeral</div>
              </div>
              <div style={sty.winPattern}>
                <div style={sty.winPatternLabel}>WIN PATTERN</div>
                <div style={sty.winPatternGrid}>
                  {Array.from({ length: 25 }, (_, i) => {
                    const s = i === 12 ? sty.winCellFree : Math.floor(i / 5) === 0 ? sty.winCellOn : sty.winCellOff;
                    return <div key={i} style={s} />;
                  })}
                </div>
                <div style={sty.winPatternName}>LINE</div>
              </div>
            </div>
            <div style={sty.boardGrid}>
              {BINGO_CELLS.map((cell, i) => (
                <div key={i} style={CELL_STYLES[cell.state]}>{cell.word}</div>
              ))}
            </div>
            <button style={sty.boardBingoBtn} disabled>BINGO!</button>
          </div>
        </div>

      </div>
    </section>
  );
}

// ── Features ──────────────────────────────────────────────────────────────────
function Features() {
  return (
    <section style={sty.featureSection}>
      <style>{`
        .feature-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .feature-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09); }
        @media (max-width: 640px) { .feature-grid { grid-template-columns: 1fr !important; } }
      `}</style>
      <div style={sty.featureSectionInner}>
        <div style={sty.featureLabel}>Why teachers love it</div>
        <h2 style={sty.featureH2}>Everything you need, nothing you don't</h2>
        <p style={sty.featureSub}>
          No new platform to learn. No student logins to manage. Just pick a list, share a code, and play.
        </p>
        <div className="feature-grid" style={sty.featureGrid}>
          {FEATURES.map((f) => (
            <div key={f.heading} className="feature-card" style={sty.featureCard}>
              <div style={{ ...sty.featureIconWrap, backgroundColor: f.iconBg }}>
                {f.icon}
              </div>
              <h3 style={sty.featureH3}>{f.heading}</h3>
              <p style={sty.featureBody}>{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── How it works ──────────────────────────────────────────────────────────────
function HowItWorks() {
  return (
    <section style={sty.howSection}>
      <style>{`
        .how-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .how-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.15); }
        @media (max-width: 640px) { .how-grid { grid-template-columns: 1fr !important; } }
      `}</style>
      <div style={sty.howInner}>
        <div style={sty.howLabel}>How it works</div>
        <h2 style={sty.howH2}>Three steps to game day</h2>
        <p style={sty.howSub}>
          The whole flow — from idea to "BINGO!" — takes less time than taking attendance.
        </p>
        <div className="how-grid" style={sty.howGrid}>
          {STEPS.map((s) => (
            <div key={s.n} className="how-card" style={sty.howCard}>
              <div style={sty.howStepNum}>{s.n}</div>
              <h3 style={sty.howCardTitle}>{s.label}</h3>
              <p style={sty.howCardBody}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Library preview ───────────────────────────────────────────────────────────
function LibraryPreview() {
  return (
    <section style={sty.libSection}>
      <style>{`
        @media (max-width: 768px) { .lib-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .lib-grid { grid-template-columns: 1fr !important; } }
      `}</style>
      <div style={sty.libInner}>
        <div style={sty.libLabel}>The library</div>
        <h2 style={sty.libH2}>A growing shelf of classroom favorites</h2>
        <p style={sty.libSub}>
          Subscribers get instant access to all titles. New novels added regularly based on teacher requests.
        </p>
        <div className="lib-grid" style={sty.libGrid}>
          {SAMPLE_NOVELS.map((n) => (
            <div key={n.title} style={sty.libCard}>
              <div style={sty.libAccentBar} />
              <div style={sty.libGrade}>{n.grade}</div>
              <div style={sty.libTitle}>{n.title}</div>
              <div style={sty.libAuthor}>{n.author}</div>
              <div style={sty.libWords}>{n.words}</div>
            </div>
          ))}
        </div>
        <div style={sty.libNote}>
          Subscribers also get unlimited custom lists — paste in any vocabulary, add definitions, and play.
        </div>
      </div>
    </section>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────
function Pricing() {
  return (
    <section style={sty.pricingSection}>
      <style>{`
        .pricing-btn-tpt { transition: background 0.15s, color 0.15s; }
        .pricing-btn-tpt:hover { background-color: #006D77 !important; color: #fff !important; }
        .pricing-btn-sub { transition: background 0.15s; }
        .pricing-btn-sub:hover { background-color: #e04f4f !important; }
        @media (max-width: 640px) { .pricing-grid { grid-template-columns: 1fr !important; } }
      `}</style>
      <div style={sty.pricingInner}>
        <div style={sty.pricingLabel}>Pricing</div>
        <h2 style={sty.pricingH2}>Simple. One price. No surprises.</h2>
        <p style={sty.pricingSub}>
          Two ways in. Pick the one that fits how you already buy teaching materials.
        </p>
        <div className="pricing-grid" style={sty.pricingGrid}>

          {/* TpT card */}
          <div style={sty.pricingCardTpt}>
            <div style={sty.pricingTier}>TpT Buyer</div>
            <div style={sty.pricingPrice}>
              Free <span style={sty.pricingPriceSub}>forever</span>
            </div>
            <p style={sty.pricingDesc}>
              Unlock any novel you've already purchased on Teachers Pay Teachers — permanently, with the code in your PDF download.
            </p>
            <ul style={sty.pricingList}>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>1 novel (your TpT purchase)</span></li>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>Unlimited games for that novel</span></li>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>Never expires</span></li>
              <li style={sty.pricingItem}><span style={sty.checkDim}>✗</span><span style={{ color: '#718096' }}>Rest of the library</span></li>
              <li style={sty.pricingItem}><span style={sty.checkDim}>✗</span><span style={{ color: '#718096' }}>Custom vocab upload</span></li>
            </ul>
            <Link to="/play?utm_source=tpt" className="pricing-btn-tpt" style={sty.pricingBtnTpt}>
              Get started with TpT
            </Link>
          </div>

          {/* Subscriber card */}
          <div style={sty.pricingCardSub}>
            <div style={sty.pricingBestValue}>Best value</div>
            <div style={sty.pricingTier}>Full Subscriber</div>
            <div style={sty.pricingPrice}>
              $19.99 <span style={sty.pricingPriceSub}>/ year</span>
            </div>
            <p style={sty.pricingDesc}>
              Cheaper than a class set of vocabulary worksheets. Covers every novel in the library and any list you create.
            </p>
            <ul style={sty.pricingList}>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>Full library — all titles</span></li>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>Custom vocabulary upload</span></li>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>Saved custom lists</span></li>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>Unlimited games</span></li>
              <li style={sty.pricingItem}><span style={sty.checkTeal}>✓</span><span>Stripe receipt for reimbursement</span></li>
            </ul>
            <Link to="/signup" className="pricing-btn-sub" style={sty.pricingBtnSub}>
              Subscribe for $19.99 / year →
            </Link>
          </div>

        </div>
        <p style={sty.pricingClosing}>Ready to make vocab review the best part of their day?</p>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={sty.footer}>
      <style>{`.footer-link:hover { color: #1B1B2F !important; }`}</style>
      <Wordmark style={{ fontSize: '20px' }} />
      <nav style={sty.footerNav}>
        <Link to="/terms"   className="footer-link" style={sty.footerLink}>Terms of Service</Link>
        <Link to="/privacy" className="footer-link" style={sty.footerLink}>Privacy Policy</Link>
        <Link to="/login"   className="footer-link" style={sty.footerLink}>Sign In</Link>
        <Link to="/demo"    className="footer-link" style={sty.footerLink}>Demo</Link>
      </nav>
      <span style={sty.footerCopy}>© {new Date().getFullYear()} Idaho Coders LLC</span>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <div style={sty.page}>
      <Nav />
      <Hero />
      <Features />
      <HowItWorks />
      <LibraryPreview />
      <Pricing />
      <Footer />
    </div>
  );
}
